import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { DEFAULT_STALE_TIME, LONG_STALE_TIME } from '../../lib';
import {
  type ExecuteLookerQueryRequest,
  type ExecuteLookerQueryResponse,
  executeLookerQuery,
  type GetLookerSuggestionsRequest,
  getLookerSuggestions,
} from '../../lib/api/looker';

export const LOOKER_QUERY_KEY = 'LOOKER_QUERY';
export const EXECUTE_LOOKER_QUERY_KEY = 'EXECUTE_LOOKER_QUERY';
export const GET_LOOKER_SUGGESTIONS_KEY = 'GET_LOOKER_SUGGESTIONS';

export const useLookerQuery = <T>(params: ExecuteLookerQueryRequest, options?: { enabled?: boolean }) => {
  const lastError = useRef(false);
  const enabled = options?.enabled === undefined || options.enabled;

  const query = useQuery({
    queryKey: [LOOKER_QUERY_KEY, params],
    queryFn: ({ signal }) => executeLookerQuery<T>(params, signal),
    enabled: enabled && !!params?.fields?.length && !!params?.lookml_model_name && !!params?.explore_name,
    staleTime: DEFAULT_STALE_TIME * 10,
    retry: 1,
    retryOnMount: false,
    placeholderData: (data) => {
      if (lastError.current) return undefined;
      return data;
    },
  });

  useEffect(() => {
    if (query.isError) lastError.current = true;
    if (query.isSuccess) lastError.current = false;
  }, [query.isError, query.isSuccess]);

  return query;
};

export const useExecuteLookerQuery = <T>() => {
  return useMutation<ExecuteLookerQueryResponse<T>, unknown, ExecuteLookerQueryRequest>({
    mutationKey: [EXECUTE_LOOKER_QUERY_KEY],
    mutationFn: (params) => executeLookerQuery<T>(params),
  });
};

export const useLookerSuggestions = (params: GetLookerSuggestionsRequest, options?: { enabled?: boolean }) => {
  const enabled = options?.enabled === undefined || options.enabled;

  return useQuery({
    queryKey: [GET_LOOKER_SUGGESTIONS_KEY, params],
    queryFn: () => getLookerSuggestions(params),
    enabled: enabled && !!params?.lookml_model_name && !!params?.explore_name && !!params.dimension_name,
    staleTime: LONG_STALE_TIME,
    placeholderData: keepPreviousData,
  });
};
